import React, { useCallback, useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import Badge from "react-bootstrap/Badge";
import Card from "react-bootstrap/Card";
import Stack from "react-bootstrap/Stack";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Button from "react-bootstrap/Button";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faTag,
    faSearch,
    faArrowDownAZ,
    faArrowDownWideShort,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";

import { AppLayout } from "../components/appLayout";
import useApiClient from "../hooks/useApiClient";
import { TagWithVideoCountViewModel } from "../apiClient";

type TagSortMode = 'name' | 'count';

export const Tags = () => {
    const [tags, setTags] = useState<TagWithVideoCountViewModel[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [sortMode, setSortMode] = useState<TagSortMode>('name');
    const apiClient = useApiClient();

    const loadData = useCallback(async () => {
        if (!apiClient) return;
        setLoading(true);
        setTags(await apiClient.tagsAll());
        setLoading(false);
    }, [apiClient]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const filteredTags = useMemo(() => {
        const trimmed = searchQuery.trim().toLowerCase();
        const filtered = trimmed
            ? tags.filter(t => (t.name ?? '').toLowerCase().includes(trimmed))
            : [...tags];

        filtered.sort((a, b) => {
            if (sortMode === 'count') {
                const countDiff = (b.videoCount ?? 0) - (a.videoCount ?? 0);
                if (countDiff !== 0) return countDiff;
            }
            return (a.name ?? '').localeCompare(b.name ?? '');
        });

        return filtered;
    }, [tags, searchQuery, sortMode]);

    return (
        <AppLayout loading={loading}>
            <Container className="py-2">
                <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center mb-3 gap-3">
                    <div>
                        <h1 className="mb-1">Tags ({tags.length})</h1>
                        <p className="text-body-secondary mb-0">
                            Browse and filter your video collection by tag.
                        </p>
                    </div>
                    {tags.length > 0 && (
                        <div className="tags-toolbar d-flex flex-wrap align-items-center justify-content-end gap-2 ms-md-auto">
                            <InputGroup size="sm" className="tags-toolbar__search">
                                <InputGroup.Text className="bg-body border-end-0 text-secondary">
                                    <FontAwesomeIcon icon={faSearch} size="sm" />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Filter tags..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="border-start-0"
                                />
                                {searchQuery && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setSearchQuery('')}
                                        aria-label="Clear tag filter"
                                    >
                                        <FontAwesomeIcon icon={faTimes} size="sm" />
                                    </Button>
                                )}
                            </InputGroup>
                            <ButtonGroup size="sm" aria-label="Sort tags" className="flex-shrink-0 ms-auto ms-sm-0">
                                <Button
                                    variant={sortMode === 'name' ? 'primary' : 'outline-secondary'}
                                    onClick={() => setSortMode('name')}
                                    title="Sort alphabetically"
                                >
                                    <FontAwesomeIcon icon={faArrowDownAZ} className="me-1" />
                                    A–Z
                                </Button>
                                <Button
                                    variant={sortMode === 'count' ? 'primary' : 'outline-secondary'}
                                    onClick={() => setSortMode('count')}
                                    title="Sort by video count"
                                >
                                    <FontAwesomeIcon icon={faArrowDownWideShort} className="me-1" />
                                    Most used
                                </Button>
                            </ButtonGroup>
                        </div>
                    )}
                </div>

                <Card>
                    <Card.Body className="p-3 p-sm-4">
                        {tags.length === 0 ? (
                            <p className="text-center text-body-secondary mb-0 py-4">No tags yet.</p>
                        ) : filteredTags.length === 0 ? (
                            <p className="text-center text-body-secondary mb-0 py-4">
                                No tags matching "{searchQuery}".
                            </p>
                        ) : (
                            <Stack direction="horizontal" gap={2} className="flex-wrap">
                                {filteredTags.map((tag) => (
                                    <Link
                                        to={`/?tag=${encodeURIComponent(tag.name ?? '')}`}
                                        key={tag.name}
                                        className="text-decoration-none tag-pill-link mw-100"
                                    >
                                        <Badge
                                            pill
                                            bg="secondary-subtle"
                                            className="tag-pill text-secondary-emphasis fw-medium px-3 py-2 d-inline-flex align-items-center gap-2 mw-100"
                                        >
                                            <FontAwesomeIcon icon={faTag} size="xs" className="text-primary flex-shrink-0" />
                                            <span className="text-truncate">{tag.name}</span>
                                            <span className="badge rounded-pill bg-body text-body-secondary border ms-1 flex-shrink-0">
                                                {tag.videoCount}
                                            </span>
                                        </Badge>
                                    </Link>
                                ))}
                            </Stack>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </AppLayout>
    );
};
